import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { Building2, LogOut, Moon, Sun, Clock, Users } from 'lucide-react';
import { ROUTES } from '../../../config/constants/routes';
import { logoutUser } from '../../../store/slices/authSlice';
import { toggleTheme } from '../../../store/slices/themeSlice';
import { LAYOUT } from '../../../constants/layout';
import { AppDispatch, RootState } from '../../../store';

const SuperAdminSidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { mode } = useSelector((state: RootState) => state.theme);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: LAYOUT.DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: LAYOUT.DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: 'none',
        },
      }}
    >
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          px: 1.5,
          py: 2,
        }}
      >
        <List sx={{ flex: 1 }}>
          <ListItem
            onClick={() => navigate('/')}
            sx={{
              py: 1.5,
              px: 2,
              mb: 1,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}08`,
                cursor: `pointer`,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Building2 size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Organizations"
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
            />
          </ListItem>
          <ListItem
            onClick={() => navigate('/pending-requests')}
            sx={{
              py: 1.5,
              px: 2,
              mb: 1,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}08`,
                cursor: `pointer`,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Clock size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Pending Organizations"
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
            />
          </ListItem>
          <ListItem
            onClick={() => navigate(ROUTES.DEALERS)}
            sx={{
              py: 1.5,
              px: 2,
              mb: 1,
              borderRadius: 2,
              backgroundColor: isActive(ROUTES.DEALERS)
                ? `${theme.palette.primary.main}15`
                : 'transparent',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}08`,
                cursor: `pointer`,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Users size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Dealers"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive(ROUTES.DEALERS) ? 600 : 400,
              }}
            />
          </ListItem>
        </List>

        {/* <Divider sx={{ my: 1 }} /> */}

        <List className="flex justify-center items-center">
          <ListItem
            onClick={handleThemeToggle}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 2,

              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}08`,
              },
            }}
            className="flex justify-center items-center"
          >
            <ListItemIcon sx={{ minWidth: 20, color: 'inherit' }}>
              {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </ListItemIcon>
          </ListItem>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 2,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: `${theme.palette.error.main}15`,
              },
            }}
            className="flex justify-center items-center"
          >
            <ListItemIcon sx={{ minWidth: 20, color: 'inherit' }}>
              <LogOut size={20} />
            </ListItemIcon>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default SuperAdminSidebar;
